import React, {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Select from 'react-select';
import { Backdrop, Fade, Grid, IconButton, Modal } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { RiCloseLine } from 'react-icons/ri';

import { useTheme } from 'styled-components';
import { BiMap } from 'react-icons/bi';
import { IDeliveryFee } from '../../../../models/DeliveryFee';
import DeliveryFeesService from '../../../../services/DeliveryFeesService';
import api from '../../../../services/api';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import {
  Wrapper,
  PageTitle,
  AddButton,
  FormWrapper,
  ModalWrapper,
  DeliveryFeeContainer,
} from './styles';
import APIError from '../../../../errors/APIError';
import ParametersService from '../../../../services/ParametersService';
import { SuggestionsCard } from '../../styles';

type ISuggestion = {
  count: number;
  addresses: {
    lat: number;
    lon: number;
  }[];
  name: string;
};

type DeliveryFeesType = {
  value: string;
  label: string;
};

const DeliveryFee: React.FC = () => {
  const theme = useTheme();
  const styles = makeStyles((them) => ({
    blue: {
      width: 46,
      height: 46,
      borderRadius: 5,
      backgroundColor: 'rgba(50, 155, 252, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(50, 155, 252, 0.3)',
      },
      [`@media only screen and (max-width: ${theme.breakpoints.lg}px)`]: {
        width: '100%',
      },
    },
    red: {
      width: 46,
      height: 46,
      borderRadius: 5,
      backgroundColor: 'rgba(246, 68, 68, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(246, 68, 68, 0.3)',
      },
      [`@media only screen and (max-width: ${theme.breakpoints.lg}px)`]: {
        width: '100%',
      },
    },
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      width: '70%',
      backgroundColor: them.palette.background.paper,
      border: '1px solid #757575',
      borderRadius: '6px',
      boxShadow: them.shadows[5],
      padding: them.spacing(2, 0, 3),
    },
    inputContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      width: '100%',
      marginBottom: 10,
    },
    inputLabel: {
      fontSize: 13,
      fontFamily: "'Sora',sans-serif",
      fontWeight: 500,
      color: '#101D40',
    },
    input: {
      backgroundColor: '#FFFFFF',
      border: '2px solid #E6EFFB',
      borderRadius: '5px',
      padding: '13px 15px',
      fontFamily: "'Sora',sans-serif",
      fontSize: '13px',
      color: '#101D40',
      width: '100%',
      transition: 'all 0.2s ease',
    },
  }))();

  const { user } = useAuth();
  const { addToast } = useToast();

  const [deliveryFees, setDeliveryFees] = useState<IDeliveryFee[]>([]);
  const [deliveryFeesType, setDeliveryFeesType] = useState<DeliveryFeesType>();
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [openSuggestions, setOpenSuggestions] = useState(false);
  const [openAddresses, setOpenAddresses] = useState(-1);
  const [deliveryFeeToDelete, setDeliveryFeeToDelete] = useState<
    IDeliveryFee
  >();
  const [deliveryFeeToUpdate, setDeliveryFeeToUpdate] = useState<
    IDeliveryFee & {
      hasDistance: boolean;
    }
  >();
  const [deliveryFeeToCreate, setDeliveryFeeToCreate] = useState<
    IDeliveryFee & {
      id?: number;
      hasDistance: boolean;
    }
  >();

  const handleCloseDeleteModal = useCallback(() => {
    setDeliveryFeeToDelete(undefined);
  }, []);

  const handleCloseUpdateModal = useCallback(() => {
    setDeliveryFeeToUpdate(undefined);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setDeliveryFeeToCreate(undefined);
  }, []);

  const handleUpdateDeliveryFee = useCallback(async () => {
    if (!deliveryFeeToUpdate) {
      return;
    }

    if (!deliveryFeeToUpdate.hasDistance && !deliveryFeeToUpdate.description) {
      addToast({
        type: 'error',
        title: 'Insira o bairro',
      });

      return;
    }

    if (
      deliveryFeeToUpdate.hasDistance &&
      deliveryFeeToUpdate.distance &&
      isNaN(deliveryFeeToUpdate.distance)
    ) {
      addToast({
        type: 'error',
        title: 'Insira uma distância válida',
      });

      return;
    }

    try {
      setIsLoading(true);

      const {
        id,
        active,
        value,
        estimatedTimeInMinutes,
        description,
        distance,
      } = deliveryFeeToUpdate;

      const updatedDeliveryFee = await DeliveryFeesService.updateDeliveryFee(
        id,
        user.empresa_id,
        {
          active,
          value,
          estimatedTimeInMinutes,
          description,
          distance,
        },
      );

      setDeliveryFees((oldState) =>
        oldState.map((deliveryFee) => {
          if (deliveryFee.id === updatedDeliveryFee.id) {
            return updatedDeliveryFee;
          }

          return deliveryFee;
        }),
      );
      setDeliveryFeeToUpdate(undefined);
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [deliveryFeeToUpdate, user.empresa_id, addToast]);

  const handleCreateDeliveryFee = useCallback(async () => {
    if (!deliveryFeeToCreate) {
      return;
    }

    if (!deliveryFeeToCreate.hasDistance && !deliveryFeeToCreate.description) {
      addToast({
        type: 'error',
        title: 'Insira o bairro',
      });

      return;
    }

    if (
      deliveryFeeToCreate.hasDistance &&
      deliveryFeeToCreate.distance &&
      isNaN(deliveryFeeToCreate.distance)
    ) {
      addToast({
        type: 'error',
        title: 'Insira uma distância válida',
      });

      return;
    }

    try {
      setIsLoading(true);

      const {
        active,
        value,
        estimatedTimeInMinutes,
        description,
        distance,
        hasDistance,
      } = deliveryFeeToCreate;

      const createdDeliveryFee = await DeliveryFeesService.createDeliveryFee(
        user.empresa_id,
        {
          active,
          value,
          estimatedTimeInMinutes,
          description: hasDistance ? `${distance} km` : description,
          distance: hasDistance ? distance : undefined,
        },
      );

      setDeliveryFees((oldState) => [...oldState, createdDeliveryFee]);
      setDeliveryFeeToCreate(undefined);
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [deliveryFeeToCreate, user.empresa_id, addToast]);

  const loadDeliveryFees = useCallback(async () => {
    try {
      setIsLoading(true);

      if (deliveryFeesType) {
        setDeliveryFees([]);

        const {
          deliveryFees: deliveryFeesList,
          suggestions: suggestionsList,
        } = await DeliveryFeesService.listDeliveryFees(
          user.empresa_id,
          `by_${deliveryFeesType.value}`,
        );
        setSuggestions(
          Object.keys(suggestionsList).map((key) => ({
            name: key,
            count: suggestionsList[key].length,
            addresses: suggestionsList[key],
          })),
        );
        setDeliveryFees(deliveryFeesList);
      }
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          type: 'error',
          title: error.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [user.empresa_id, deliveryFeesType, addToast]);

  const deleteSuggestions = useCallback(async () => {
    try {
      await DeliveryFeesService.deleteSuggestions(user.empresa_id);

      setSuggestions([]);
      setOpenSuggestions(false);
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          type: 'error',
          title: error.message,
        });
      }
    }
  }, [user.empresa_id, addToast]);

  const loadDeliveryFeesType = useCallback(async () => {
    try {
      setIsLoading(true);

      const parameterDetails = await ParametersService.showParameter(
        'taxa_bairro',
        user.empresa_id,
      );

      let value: string;
      let label: string;

      if (parameterDetails.role.status) {
        value = 'neighborhood';
        label = 'Taxa por bairro';
      } else {
        value = 'distance';
        label = 'Taxa por distância';
      }

      setDeliveryFeesType({
        value,
        label,
      });
    } catch (error) {
      setDeliveryFeesType({
        value: 'distance',
        label: 'Taxa por distância',
      });
    } finally {
      setIsLoading(false);
    }
  }, [user.empresa_id]);

  const handleDeliveryFeesTypeChange = useCallback(
    async (selectedOption: DeliveryFeesType): Promise<void> => {
      if (deliveryFeesType && deliveryFeesType.value === selectedOption.value) {
        return;
      }

      try {
        setIsLoading(true);

        await api.post('/parametro', {
          empresa_id: user.empresa_id,
          descricao: 'taxa_bairro',
          role: JSON.stringify({
            status: selectedOption.value === 'neighborhood',
          }),
        });

        setDeliveryFeesType(selectedOption);
      } catch (error) {
        if (error instanceof APIError) {
          addToast({
            type: 'error',
            title: error.message,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [user.empresa_id, deliveryFeesType, addToast],
  );

  async function handleDeleteDeliveryFee(id: number): Promise<void> {
    const deletedDeliveryFee = deliveryFees.find(
      (deliveryFee) => deliveryFee.id === id,
    );

    if (!deletedDeliveryFee) {
      return;
    }

    setDeliveryFees((oldState) =>
      oldState.filter((deliveryFee) => deliveryFee.id !== id),
    );

    handleCloseDeleteModal();

    try {
      await DeliveryFeesService.deleteDeliveryFee(id, user.empresa_id);

      addToast({
        title: 'Taxa excluída com sucesso',
        type: 'success',
      });
    } catch (error) {
      if (error instanceof APIError) {
        setDeliveryFees((oldState) => [...oldState, deletedDeliveryFee]);
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    }
  }

  function handleUpdateDeliveryFeeFields(
    fieldName: keyof IDeliveryFee,
    value: any,
  ): void {
    setDeliveryFeeToUpdate((oldState) => {
      if (!oldState) {
        return undefined;
      }

      return {
        ...oldState,
        [fieldName]: value,
      };
    });
  }

  function handleCreateDeliveryFeeFields(
    fieldName: keyof IDeliveryFee,
    value: any,
  ): void {
    setDeliveryFeeToCreate((oldState) => {
      if (!oldState) {
        return undefined;
      }

      return {
        ...oldState,
        [fieldName]: value,
      };
    });
  }

  useEffect(() => {
    loadDeliveryFeesType();
  }, [loadDeliveryFeesType]);

  useEffect(() => {
    if (deliveryFeesType) {
      loadDeliveryFees();
    }
  }, [deliveryFeesType, loadDeliveryFees]);

  return (
    <Wrapper>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle>Taxas de entrega</PageTitle>
        </Grid>
        <Grid item xs={12}>
          <Select
            name="delivery-tax-method-select"
            placeholder="Selecione o método de taxa de entrega"
            isDisabled={isLoading}
            options={[
              { value: 'distance', label: 'Taxa por distância' },
              { value: 'neighborhood', label: 'Taxa por bairro' },
            ]}
            value={deliveryFeesType}
            onChange={(selectedOption: any) => {
              handleDeliveryFeesTypeChange(selectedOption);
            }}
          />
        </Grid>

        {deliveryFeesType && (
          <Grid
            item
            xs={12}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <AddButton
              type="button"
              disabled={isLoading}
              onClick={() => {
                setDeliveryFeeToCreate({
                  id: 0,
                  active: true,
                  estimatedTimeInMinutes: 0,
                  value: 0,
                  hasDistance: deliveryFeesType.value === 'distance',
                  description: '',
                  distance:
                    deliveryFeesType.value === 'distance' ? 0 : undefined,
                });
              }}
            >
              + Adicionar Taxa
            </AddButton>
            {suggestions.filter(
              (suggestion) =>
                !deliveryFees.find((df) => df.description === suggestion.name),
            ).length >= 1 && (
              <button
                style={{
                  display: 'flex',
                  padding: '8px 12px',
                  borderRadius: 5,
                  backgroundColor: '#4262ff',
                  color: '#ffffff',
                  border: 'none',
                  alignItems: 'center',
                  gap: 4,
                }}
                type="button"
                onClick={() => setOpenSuggestions(true)}
              >
                <span
                  style={{ color: '#ffffff', fontSize: '20px' }}
                  className="material-icons-outlined"
                >
                  lightbulb
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '13px',
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Sugestões
                </span>
              </button>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <FormWrapper>
            <Grid container spacing={3}>
              {deliveryFeesType ? (
                <>
                  {deliveryFeesType.value === 'distance'
                    ? deliveryFees.map((deliveryFee) => (
                        <Fragment key={deliveryFee.id}>
                          <DeliveryFeeContainer>
                            <div>
                              <label htmlFor={`distance_${deliveryFee.id}`}>
                                Distância (km)
                              </label>

                              <input
                                id={`distance_${deliveryFee.id}`}
                                name="distance"
                                type="number"
                                value={deliveryFee.distance}
                                disabled
                              />
                            </div>

                            <div>
                              <label htmlFor={`fee_${deliveryFee.id}`}>
                                Taxa
                              </label>

                              <input
                                id={`fee_${deliveryFee.id}`}
                                name="fee"
                                type="number"
                                value={deliveryFee.value}
                                disabled
                              />
                            </div>

                            <div>
                              <label htmlFor={`duration_${deliveryFee.id}`}>
                                Tempo (mins)
                              </label>

                              <input
                                id={`duration_${deliveryFee.id}`}
                                name="duration"
                                type="number"
                                value={deliveryFee.estimatedTimeInMinutes}
                                disabled
                              />
                            </div>

                            <span
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 10,
                              }}
                            >
                              <IconButton
                                color="inherit"
                                className={styles.blue}
                                disabled={isLoading}
                                onClick={() =>
                                  setDeliveryFeeToUpdate({
                                    ...deliveryFee,
                                    hasDistance:
                                      typeof deliveryFee?.distance === 'number',
                                  })
                                }
                              >
                                <span
                                  style={{ color: '#329BFC', fontSize: '25px' }}
                                  className="material-icons-outlined"
                                >
                                  edit
                                </span>
                              </IconButton>
                              <IconButton
                                color="inherit"
                                className={styles.red}
                                disabled={isLoading}
                                onClick={() =>
                                  setDeliveryFeeToDelete(deliveryFee)
                                }
                              >
                                <span
                                  style={{ color: '#F64444', fontSize: '25px' }}
                                  className="material-icons-outlined"
                                >
                                  delete
                                </span>
                              </IconButton>
                            </span>
                          </DeliveryFeeContainer>
                        </Fragment>
                      ))
                    : deliveryFees.map((deliveryFee) => (
                        <Fragment key={deliveryFee.id}>
                          <DeliveryFeeContainer>
                            <div>
                              <label htmlFor={`neighborhood_${deliveryFee.id}`}>
                                Bairro
                              </label>

                              <input
                                id={`neighborhood_${deliveryFee.id}`}
                                name="neighborhood"
                                value={deliveryFee.description}
                                disabled
                              />
                            </div>

                            <div>
                              <label htmlFor={`fee_${deliveryFee.id}`}>
                                Taxa
                              </label>

                              <input
                                id={`fee_${deliveryFee.id}`}
                                name="fee"
                                type="number"
                                value={deliveryFee.value}
                                disabled
                              />
                            </div>

                            <div>
                              <label htmlFor={`duration_${deliveryFee.id}`}>
                                Tempo (mins)
                              </label>

                              <input
                                id={`duration_${deliveryFee.id}`}
                                name="duration"
                                type="number"
                                value={deliveryFee.estimatedTimeInMinutes}
                                disabled
                              />
                            </div>

                            <span
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 10,
                              }}
                            >
                              <IconButton
                                color="inherit"
                                className={styles.blue}
                                disabled={isLoading}
                                onClick={() =>
                                  setDeliveryFeeToUpdate({
                                    ...deliveryFee,
                                    hasDistance:
                                      typeof deliveryFee?.distance === 'number',
                                  })
                                }
                              >
                                <span
                                  style={{ color: '#329BFC', fontSize: '25px' }}
                                  className="material-icons-outlined"
                                >
                                  edit
                                </span>
                              </IconButton>
                              <IconButton
                                color="inherit"
                                className={styles.red}
                                disabled={isLoading}
                                onClick={() =>
                                  setDeliveryFeeToDelete(deliveryFee)
                                }
                              >
                                <span
                                  style={{ color: '#F64444', fontSize: '25px' }}
                                  className="material-icons-outlined"
                                >
                                  delete
                                </span>
                              </IconButton>
                            </span>
                          </DeliveryFeeContainer>
                        </Fragment>
                      ))}
                </>
              ) : null}
            </Grid>
          </FormWrapper>
        </Grid>
      </Grid>
      <Modal
        className={styles.modal}
        open={openSuggestions}
        onClose={() => {
          setOpenSuggestions(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openSuggestions}>
          <ModalWrapper
            style={{
              paddingBottom: 30,
              width: '90%',
              maxWidth: 700,
              overflow: 'auto',
              maxHeight: '90%',
            }}
          >
            <>
              <div className="header">
                <h1>Sugestões</h1>

                <button
                  type="button"
                  onClick={() => {
                    setOpenSuggestions(false);
                  }}
                >
                  <RiCloseLine size={30} />
                </button>
              </div>

              <div className="suggestions">
                {suggestions
                  .sort((a, b) => b.count - a.count)
                  .filter(
                    (suggestion) =>
                      !deliveryFees.find(
                        (df) => df.description === suggestion.name,
                      ),
                  )
                  .map((suggestion, suggestionIndex) => (
                    <SuggestionsCard
                      type="button"
                      key={suggestion.name}
                      onClick={() => {
                        setOpenAddresses(suggestionIndex);
                      }}
                    >
                      <div className="icon-container">
                        <BiMap size={22} />
                      </div>
                      <div className="content">
                        <h3>{suggestion.name}</h3>
                        <h4>{suggestion.count} solicitações</h4>
                      </div>
                      {!suggestionIndex && (
                        <div className="badge">
                          <p>Mais solicitado</p>
                        </div>
                      )}
                    </SuggestionsCard>
                  ))}
              </div>
              <button
                type="button"
                style={{
                  width: '100%',
                  padding: 12,
                  marginTop: 20,
                  border: 'none',
                  backgroundColor: 'rgb(246, 68, 68)',
                  borderRadius: 4,
                  color: '#ffffff',
                  fontWeight: 600,
                  fontFamily: 'Sora',
                }}
                onClick={deleteSuggestions}
              >
                Limpar bairros
              </button>
            </>
          </ModalWrapper>
        </Fade>
      </Modal>
      <Modal
        className={styles.modal}
        open={openAddresses >= 0}
        onClose={() => {
          setOpenAddresses(-1);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openAddresses >= 0}>
          <ModalWrapper
            style={{
              paddingBottom: 30,
              width: '90%',
              maxWidth: 700,
              overflow: 'auto',
              maxHeight: '90%',
            }}
          >
            <>
              <div className="header">
                <h1>Localizações</h1>

                <button
                  type="button"
                  onClick={() => {
                    setOpenAddresses(-1);
                  }}
                >
                  <RiCloseLine size={30} />
                </button>
              </div>

              <div className="suggestions">
                {openAddresses >= 0 &&
                suggestions[openAddresses].addresses.length > 0
                  ? suggestions[openAddresses].addresses
                      .filter(
                        (address, addressIndex, addressList) =>
                          addressList.findIndex(
                            ({ lat, lon }) =>
                              lat === address.lat && lon === address.lon,
                          ) === addressIndex,
                      )
                      .map(({ lat, lon }, addressIndex) => (
                        <a
                          key={`address-location-${lat}-${lon}`}
                          style={{
                            border: '2px solid rgb(50, 155, 252)',
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px 8px',
                            textDecoration: 'none',
                            fontWeight: 400,
                            cursor: 'pointer',
                            color: '#212121',
                          }}
                          href={`https://www.google.com/maps/dir/?api=1&origin=${user.empresa.lat}%2C${user.empresa.lon}&destination=${lat}%2C${lon}&travelmode=driving`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Localização{' '}
                          <i style={{ fontWeight: 600, marginLeft: 5 }}>
                            #{String(addressIndex + 1).padStart(2, '0')}
                          </i>
                        </a>
                      ))
                  : null}
              </div>
              {deliveryFeesType && deliveryFeesType.value === 'neighborhood' && (
                <button
                  type="button"
                  style={{
                    width: '100%',
                    padding: 12,
                    marginTop: 20,
                    border: 'none',
                    backgroundColor: '#329BFC',
                    borderRadius: 4,
                    color: '#ffffff',
                    fontWeight: 600,
                    fontFamily: 'Sora',
                  }}
                  onClick={() => {
                    setOpenAddresses(-1);
                    setOpenSuggestions(false);
                    setDeliveryFeeToCreate({
                      id: 0,
                      active: true,
                      estimatedTimeInMinutes: 0,
                      value: 0,
                      hasDistance: false,
                      description: suggestions[openAddresses].name,
                      distance: undefined,
                    });
                  }}
                >
                  Adicionar bairro
                </button>
              )}
            </>
          </ModalWrapper>
        </Fade>
      </Modal>
      <Modal
        className={styles.modal}
        open={!!deliveryFeeToDelete}
        onClose={handleCloseDeleteModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={!!deliveryFeeToDelete}>
          <ModalWrapper>
            {deliveryFeeToDelete && (
              <>
                <div className="header">
                  <h1>Exclusão</h1>

                  <button type="button" onClick={handleCloseDeleteModal}>
                    <RiCloseLine size={30} />
                  </button>
                </div>

                <div className="description">
                  <span>
                    Deseja excluir a taxa de{' '}
                    {deliveryFeeToDelete?.distance
                      ? `${deliveryFeeToDelete.distance} KM`
                      : `${deliveryFeeToDelete?.description}`}
                    ?
                  </span>
                </div>

                <div className="btnActions">
                  <button
                    type="button"
                    className="confirm"
                    disabled={isLoading}
                    onClick={() =>
                      handleDeleteDeliveryFee(deliveryFeeToDelete.id)
                    }
                  >
                    Confirmar
                  </button>

                  <button
                    type="button"
                    className="cancel"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </ModalWrapper>
        </Fade>
      </Modal>
      <Modal
        className={styles.modal}
        open={!!deliveryFeeToUpdate}
        onClose={handleCloseUpdateModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={!!deliveryFeeToUpdate}>
          <ModalWrapper>
            {deliveryFeeToUpdate && (
              <>
                <div className="header">
                  <h1>Edição</h1>

                  <button type="button" onClick={handleCloseUpdateModal}>
                    <RiCloseLine size={30} />
                  </button>
                </div>

                {deliveryFeeToUpdate?.hasDistance ? (
                  <>
                    <div className={styles.inputContainer}>
                      <label
                        htmlFor={`distance_${deliveryFeeToUpdate.id}`}
                        className={styles.inputLabel}
                      >
                        Distância (km)
                      </label>

                      <input
                        id={`distance_${deliveryFeeToUpdate.id}`}
                        name="distance"
                        className={styles.input}
                        type="number"
                        step="1"
                        min={0}
                        value={deliveryFeeToUpdate.distance}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateDeliveryFeeFields(
                            'distance',
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.inputContainer}>
                      <label
                        htmlFor={`neighborhood_${deliveryFeeToUpdate.id}`}
                        className={styles.inputLabel}
                      >
                        Bairro
                      </label>

                      <input
                        id={`neighborhood_${deliveryFeeToUpdate.id}`}
                        name="neighborhood"
                        className={styles.input}
                        value={deliveryFeeToUpdate.description}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateDeliveryFeeFields(
                            'description',
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </>
                )}

                <div className={styles.inputContainer}>
                  <label
                    htmlFor={`fee_${deliveryFeeToUpdate.id}`}
                    className={styles.inputLabel}
                  >
                    Taxa
                  </label>

                  <input
                    id={`fee_${deliveryFeeToUpdate.id}`}
                    name="fee"
                    className={styles.input}
                    type="number"
                    value={deliveryFeeToUpdate.value}
                    step=".01"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateDeliveryFeeFields('value', e.target.value)
                    }
                  />
                </div>

                <div className={styles.inputContainer}>
                  <label
                    htmlFor={`duration_${deliveryFeeToUpdate.id}`}
                    className={styles.inputLabel}
                  >
                    Tempo (mins)
                  </label>

                  <input
                    id={`duration_${deliveryFeeToUpdate.id}`}
                    className={styles.input}
                    name="duration"
                    type="number"
                    step="1"
                    value={deliveryFeeToUpdate.estimatedTimeInMinutes}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateDeliveryFeeFields(
                        'estimatedTimeInMinutes',
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="btnActions">
                  <button
                    type="button"
                    disabled={isLoading}
                    className="confirm"
                    onClick={handleUpdateDeliveryFee}
                  >
                    Confirmar
                  </button>

                  <button
                    type="button"
                    className="cancel"
                    onClick={handleCloseUpdateModal}
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </ModalWrapper>
        </Fade>
      </Modal>
      <Modal
        className={styles.modal}
        open={!!deliveryFeeToCreate}
        onClose={handleCloseCreateModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={!!deliveryFeeToCreate}>
          <ModalWrapper>
            {deliveryFeeToCreate && (
              <>
                <div className="header">
                  <h1>Cadastro</h1>

                  <button type="button" onClick={handleCloseCreateModal}>
                    <RiCloseLine size={30} />
                  </button>
                </div>

                {deliveryFeeToCreate?.hasDistance ? (
                  <>
                    <div className={styles.inputContainer}>
                      <label
                        htmlFor="distance_create"
                        className={styles.inputLabel}
                      >
                        Distância (km)
                      </label>

                      <input
                        id="distance_create"
                        name="distance"
                        className={styles.input}
                        type="number"
                        step="1"
                        min={0}
                        value={deliveryFeeToCreate.distance}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleCreateDeliveryFeeFields(
                            'distance',
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.inputContainer}>
                      <label
                        htmlFor="neighborhood_create"
                        className={styles.inputLabel}
                      >
                        Bairro
                      </label>

                      <input
                        id="neighborhood_create"
                        name="neighborhood"
                        className={styles.input}
                        value={deliveryFeeToCreate.description}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleCreateDeliveryFeeFields(
                            'description',
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </>
                )}

                <div className={styles.inputContainer}>
                  <label htmlFor="fee_create" className={styles.inputLabel}>
                    Taxa
                  </label>

                  <input
                    id="fee_create"
                    name="fee"
                    className={styles.input}
                    type="number"
                    value={deliveryFeeToCreate.value}
                    step=".01"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleCreateDeliveryFeeFields('value', e.target.value)
                    }
                  />
                </div>

                <div className={styles.inputContainer}>
                  <label
                    htmlFor="duration_create"
                    className={styles.inputLabel}
                  >
                    Tempo (mins)
                  </label>

                  <input
                    id="duration_create"
                    className={styles.input}
                    name="duration"
                    type="number"
                    step="1"
                    value={deliveryFeeToCreate.estimatedTimeInMinutes}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleCreateDeliveryFeeFields(
                        'estimatedTimeInMinutes',
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="btnActions">
                  <button
                    type="button"
                    className="confirm"
                    disabled={isLoading}
                    onClick={handleCreateDeliveryFee}
                  >
                    Confirmar
                  </button>

                  <button
                    type="button"
                    className="cancel"
                    onClick={handleCloseCreateModal}
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </ModalWrapper>
        </Fade>
      </Modal>
    </Wrapper>
  );
};

export default DeliveryFee;
